import React from 'react'
import * as Sentry from '@sentry/browser'
import Button from 'base-components/Button'

/**
 * Per-route error boundary. Contains failures to a single route's content area
 * — sidebar and header stay functional, so users can still navigate elsewhere.
 *
 * Pairs with the global ErrorBoundary at the app root (src/index.tsx), which
 * remains as the backstop for failures outside a route (providers, layout,
 * shared header/sidebar).
 *
 * Background: FLEXIME-AH and the DrawingManager outage (2026-06-05) both
 * surfaced the same way — a single component throw turned the whole app into
 * "Något gick fel". Per-route boundaries reduce the blast radius so a broken
 * /geofences doesn't take down /notifications, /fileImport, etc.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { name?: string; message?: string }
  const msg = e.message ?? ''
  return (
    e.name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed')
  )
}

interface State {
  hasError: boolean
  isChunkLoad: boolean
}

export default class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, isChunkLoad: false }
  }

  static getDerivedStateFromError(error: unknown): State {
    Sentry.captureException(error, { tags: { boundary: 'route' } })
    return { hasError: true, isChunkLoad: isChunkLoadError(error) }
  }

  componentDidCatch(error: unknown, errorInfo: { componentStack?: string | null }) {
    console.error('[RouteErrorBoundary] Caught error:', error)
    console.error('[RouteErrorBoundary] Component stack:', errorInfo?.componentStack)
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.state.isChunkLoad) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800">Flexime har uppdaterats</h2>
          <p className="mt-3 text-gray-600 max-w-md">
            Den här sidan kunde inte laddas eftersom Flexime har uppdaterats. Ladda om för att
            fortsätta.
          </p>
          <Button onClick={this.handleReload} className="mt-5">
            Ladda om sidan
          </Button>
        </div>
      )
    }

    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800">Något gick fel på den här sidan</h2>
        <p className="mt-3 text-gray-600 max-w-md">
          Resten av Flexime fungerar fortfarande — använd menyn för att gå till en annan sida.
          Felet har rapporterats. Om problemet kvarstår, kontakta support.
        </p>
        <Button onClick={this.handleReload} className="mt-5" variant="secondary">
          Ladda om sidan
        </Button>
      </div>
    )
  }
}
